import { Box } from '@deity/falcon-ui';
import { type IPropertyBag } from '@deity-io/di-data-basics';
import type {
  DictDefinition,
  FieldDefinition,
  LinkDefinition,
  ListDefinition
} from '@deity-io/di-data-structure-editor';
import { DefinitionFactory, DefinitionLoader } from '@deity-io/di-data-structure-editor';
import { SchemaContext } from 'contexts/SchemaContext';
import _ from 'lodash';
import { useContext, useMemo, useState } from 'react';
import type { FC } from 'react';
import ConstraintsContainer from '../FieldEditor/EditConstraints/ConstraintsContainer';
import EditFieldDefaults from '../FieldEditor/EditFieldDefaults';
import EditLinkFields from '../FieldEditor/EditLinkFields';
import FieldInfo from '../FieldEditor/FieldInfo';
import FieldNameEditor from '../FieldEditor/FieldNameEditor';
import FieldTypeEditor from '../FieldEditor/FieldTypeEditor';
import LinkInfo from '../FieldEditor/LinkInfo';

interface EditFieldPanelProps {}

const EditFieldContainer: FC<EditFieldPanelProps> = () => {
  const ctx = useContext(SchemaContext);
  const [availableTypes] = useState(() => ['dict', 'list', 'link', ...DefinitionFactory.availableFieldTypes()]);
  const fieldInfo = ctx?.helper.getSelectedFieldInfo();
  const isEditable = ctx?.state.locked !== true && (fieldInfo?.isLookupSource !== true || fieldInfo?.isLink === true);

  const schema = useMemo(
    () =>
      fieldInfo
        ? DefinitionLoader.tryLoad<FieldDefinition | DictDefinition | ListDefinition>(
            fieldInfo.name,
            fieldInfo.fieldJson
          )
        : undefined,
    [fieldInfo?.name, fieldInfo?.fieldJson]
  );

  let linkSchema: LinkDefinition | undefined;
  const linkEditable = _.get(ctx?.state.schema, ctx?.state?.selectedField || []) !== undefined;
  if (fieldInfo?.isLink === true) {
    linkSchema = DefinitionLoader.tryLoad<LinkDefinition>(
      fieldInfo.name,
      fieldInfo.source?.link?.schema as IPropertyBag
    );
  }

  return fieldInfo && schema ? (
    <Box p="md" width="full" css={{ height: '100%' }}>
      <Box p="xs" mb="xs" key="info">
        <FieldInfo
          type={'fieldType' in schema ? schema.fieldType.type : schema.coreType}
          fieldName={fieldInfo.name}
          fieldPath={fieldInfo.path}
        />

        <FieldNameEditor fieldName={fieldInfo.name} fieldPath={fieldInfo.path} isEditable={isEditable} />
        <FieldTypeEditor
          type={fieldInfo.settings.type}
          availableTypes={availableTypes}
          fieldPath={fieldInfo.path}
          isEditable={isEditable && !fieldInfo.isLink}
        />
      </Box>

      {fieldInfo?.isLookupSource && fieldInfo?.isLink === false && (
        <LinkInfo
          key="LinkInfo"
          fieldName={fieldInfo.source!.name}
          fieldPath={fieldInfo.path.slice(fieldInfo.source?.path?.length || 0)}
        />
      )}
      {fieldInfo.isLink && (
        <EditLinkFields
          key="editLinkFields"
          path={fieldInfo.path}
          schema={linkSchema!}
          locked={isEditable !== true || !linkEditable}
        />
      )}

      {!(fieldInfo.isLink && !fieldInfo.isLookupSource) && (
        <EditFieldDefaults
          key="keyDefaults"
          path={fieldInfo.path}
          schema={schema}
          locked={isEditable !== true}
          link={linkSchema}
        />
      )}

      <ConstraintsContainer
        key="constraints"
        path={fieldInfo.path}
        schema={schema}
        isEditable={isEditable}
        link={linkSchema}
      />
    </Box>
  ) : null;
};

export default EditFieldContainer;
